import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Papa from 'papaparse';
import { StageProps, SeriesParticipant } from '../types';

const ResultsPreview: React.FC<StageProps> = ({ appData, setAppData, onPrev, onNext, onRestart }) => {
  const [finalResults, setFinalResults] = useState<SeriesParticipant[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Enhanced results management state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'totalPoints' | 'position'>('totalPoints');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'table' | 'comparison' | 'analytics'>('table');
  const [selectedRaces, setSelectedRaces] = useState<string[]>([]);
  
  // Best-of configuration state
  const [bestOfCount, setBestOfCount] = useState<number | undefined>(undefined);
  const [useBestOf, setUseBestOf] = useState(false);
  const [configInitialized, setConfigInitialized] = useState(false);

  const generateFinalResults = useCallback(() => {
    if (!appData.processedRaceData || isGenerating) return;

    setIsGenerating(true);
    setLoading(true);

    try {
      const existingParticipants = appData.existingSeriesData || [];
      const matchedParticipants = appData.matchedParticipants || [];
      const raceId = appData.newRaceData?.raceName || `race_${Date.now()}`;
      const raceDate = appData.newRaceData?.raceDate || new Date().toISOString().split('T')[0];

      console.log('=== GENERATING FINAL RESULTS ===');
      console.log('Existing participants:', existingParticipants.length);
      console.log('Sample existing participant:', existingParticipants[0]);
      console.log('Matched participants:', matchedParticipants.length);
      console.log('New race ID:', raceId);

      // Create a map of existing participants
      const existingMap = new Map<string, SeriesParticipant>();
      existingParticipants.forEach(p => {
        existingMap.set(p.id, { ...p });
      });

      // Process each race participant
      appData.processedRaceData.forEach((raceParticipant, index) => {
        const matchCandidate = matchedParticipants.find(m => 
          m.newParticipant.name === raceParticipant.name
        );

        let seriesParticipant: SeriesParticipant;

        if (matchCandidate?.selectedMatch && matchCandidate.status !== 'new') {
          // Update existing participant
          seriesParticipant = existingMap.get(matchCandidate.selectedMatch.id) || {
            ...matchCandidate.selectedMatch
          };
        } else {
          // Create new participant - preserve ALL fields from race participant
          seriesParticipant = {
            id: `participant_${Date.now()}_${index}`,
            name: raceParticipant.name,
            category: raceParticipant.category || '',
            license: raceParticipant.license || '',
            races: {},
            totalPoints: 0,
            bestRaces: [],
            // Preserve all additional fields from the original race data
            ...Object.keys(raceParticipant).reduce((acc, key) => {
              if (!['id', 'name', 'category', 'license', 'position', 'points'].includes(key)) {
                acc[key] = raceParticipant[key];
              }
              return acc;
            }, {} as Record<string, any>)
          };
        }

        // Add this race's results
        seriesParticipant.races[raceId] = {
          position: raceParticipant.position || 999,
          points: raceParticipant.points || 0,
          date: raceDate
        };

        // Recalculate total points
        seriesParticipant.totalPoints = Object.values(seriesParticipant.races)
          .reduce((sum, race) => sum + race.points, 0);

        // Apply Best-of-X configuration if present (check both sources)
        const bestOfCount = appData.seriesConfiguration?.bestOfCount || appData.currentSeriesMetadata?.bestOfRaces;
        if (bestOfCount) {
          const raceEntries = Object.entries(seriesParticipant.races);
          const bestRaces = raceEntries
            .sort(([, a], [, b]) => b.points - a.points)
            .slice(0, bestOfCount);
          
          seriesParticipant.bestRaces = bestRaces.map(([raceId]) => raceId);
          seriesParticipant.bestOfPoints = bestRaces.reduce((sum, [, race]) => sum + race.points, 0);
        } else {
          seriesParticipant.bestRaces = Object.keys(seriesParticipant.races);
          seriesParticipant.bestOfPoints = seriesParticipant.totalPoints;
        }

        existingMap.set(seriesParticipant.id, seriesParticipant);
      });

      const results = Array.from(existingMap.values())
        .sort((a, b) => {
          // Sort by best-of points if configured, otherwise total points
          const aPoints = a.bestOfPoints || a.totalPoints;
          const bPoints = b.bestOfPoints || b.totalPoints;
          return bPoints - aPoints;
        });

      console.log('Final results sorted by total points:', results.map(r => ({ name: r.name, totalPoints: r.totalPoints, races: Object.keys(r.races) })));

      setFinalResults(results);
      setAppData({
        ...appData,
        finalResults: results
      });
    } catch (error) {
      console.error('Error generating final results:', error);
    } finally {
      setLoading(false);
      setIsGenerating(false);
    }
  }, [appData, isGenerating, setAppData]);

  useEffect(() => {
    generateFinalResults();
  }, [generateFinalResults]);

  // Initialize best-of settings from existing configuration (only run once per series)
  useEffect(() => {
    // Reset initialization flag when series changes
    if (appData.currentSeriesMetadata?.id) {
      setConfigInitialized(false);
    }
  }, [appData.currentSeriesMetadata?.id]);

  // Separate effect for loading configuration (only runs once per series)
  useEffect(() => {
    if (configInitialized || !appData.currentSeriesMetadata?.id) return;
    
    // Priority 1: Load from current series metadata (most authoritative)
    if (appData.currentSeriesMetadata?.bestOfRaces) {
      console.log('Loading best-of configuration from series metadata:', appData.currentSeriesMetadata.bestOfRaces);
      setUseBestOf(true);
      setBestOfCount(appData.currentSeriesMetadata.bestOfRaces);
      
      // Ensure appData.seriesConfiguration is synced for generateFinalResults
      setAppData({
        ...appData,
        seriesConfiguration: {
          ...appData.seriesConfiguration,
          bestOfCount: appData.currentSeriesMetadata.bestOfRaces
        }
      });
    }
    // Priority 2: Fall back to appData.seriesConfiguration
    else if (appData.seriesConfiguration?.bestOfCount) {
      console.log('Loading best-of configuration from app data:', appData.seriesConfiguration.bestOfCount);
      setUseBestOf(true);
      setBestOfCount(appData.seriesConfiguration.bestOfCount);
    }
    // Priority 3: No best-of configuration
    else {
      console.log('No best-of configuration found, using all races');
      setUseBestOf(false);
      setBestOfCount(undefined);
    }
    
    setConfigInitialized(true);
  }, [configInitialized, appData, setAppData]);

  // Initialize data when coming from Series Overview
  useEffect(() => {
    if (!finalResults && appData.existingSeriesData && appData.existingSeriesData.length > 0) {
      console.log('ResultsPreview: Initializing from existing series data');
      console.log('Existing series data count:', appData.existingSeriesData.length);
      
      // Sort by best-of points if configured, otherwise total points
      const sortedResults = [...appData.existingSeriesData].sort((a, b) => {
        const aPoints = a.bestOfPoints || a.totalPoints;
        const bPoints = b.bestOfPoints || b.totalPoints;
        return bPoints - aPoints;
      });
      
      setFinalResults(sortedResults);
      setAppData({
        ...appData,
        finalResults: sortedResults
      });
    }
  }, [appData, finalResults, setAppData]);

  const exportToCSV = () => {
    if (!finalResults) return;

    // Create a standardized CSV format for series storage and manipulation
    // This format is designed to be:
    // 1. Human-readable and editable in spreadsheet applications
    // 2. Machine-parseable for re-importing into the application
    // 3. Comprehensive with all race data for complete series reconstruction
    // 4. Consistent column structure regardless of number of races
    const csvData = finalResults.map((participant, index) => {
      const row: any = {
        // Core participant info
        seriesPosition: index + 1,
        participantId: participant.id,
        name: participant.name,
        category: participant.category,
        license: participant.license,
        totalPoints: participant.totalPoints,
        
        // Race-specific columns (dynamic based on races in series)
        ...Object.entries(participant.races).reduce((raceColumns, [raceId, raceData]) => {
          raceColumns[`${raceId}_position`] = raceData.position;
          raceColumns[`${raceId}_points`] = raceData.points;
          raceColumns[`${raceId}_date`] = raceData.date;
          return raceColumns;
        }, {} as Record<string, any>)
      };

      return row;
    });

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `series_standings_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const exportRaceResults = () => {
    if (!appData.processedRaceData) return;

    const csvData = appData.processedRaceData
      .sort((a, b) => (a.position || 999) - (b.position || 999))
      .map(participant => ({
        position: participant.position,
        name: participant.name,
        category: participant.category,
        license: participant.license,
        points: participant.points,
        raceDate: appData.newRaceData?.raceDate || new Date().toISOString().split('T')[0],
        raceName: appData.newRaceData?.raceName || 'Latest Race'
      }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `race_results_${appData.newRaceData?.raceName || 'latest'}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Get unique categories for filtering
  const availableCategories = useMemo(() => {
    if (!finalResults) return [];
    return Array.from(new Set(finalResults.map(p => p.category))).sort();
  }, [finalResults]);

  const getRaceColumns = useCallback(() => {
    if (!finalResults || finalResults.length === 0) return [];
    
    const allRaces = new Set<string>();
    finalResults.forEach(participant => {
      Object.keys(participant.races).forEach(raceId => allRaces.add(raceId));
    });
    
    return Array.from(allRaces).sort((a, b) => {
      const sampleParticipant = finalResults.find(p => p.races[a] && p.races[b]);
      if (sampleParticipant) {
        const dateA = sampleParticipant.races[a].date;
        const dateB = sampleParticipant.races[b].date;
        if (dateA && dateB) {
          return new Date(dateA).getTime() - new Date(dateB).getTime();
        }
      }
      return a.localeCompare(b);
    });
  }, [finalResults]);



  // Group results by category for display with category-specific rankings
  const groupedResults = finalResults ? 
    finalResults.reduce((groups, participant) => {
      const category = participant.category || 'Uncategorized';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(participant);
      return groups;
    }, {} as Record<string, SeriesParticipant[]>) : {};

  // Helper function to get the appropriate points value based on best-of configuration
  const getEffectivePoints = useCallback((participant: SeriesParticipant) => {
    return participant.bestOfPoints || participant.totalPoints;
  }, []);

  // Helper function to get current best-of configuration from either source
  const getCurrentBestOfCount = useCallback(() => {
    return appData.seriesConfiguration?.bestOfCount || appData.currentSeriesMetadata?.bestOfRaces;
  }, [appData.seriesConfiguration?.bestOfCount, appData.currentSeriesMetadata?.bestOfRaces]);

  // Sort each category by effective points (best-of or total) and assign category-specific positions
  const sortedCategories = Object.keys(groupedResults).sort();
  sortedCategories.forEach(category => {
    groupedResults[category].sort((a, b) => getEffectivePoints(b) - getEffectivePoints(a));
  });

  // Calculate analytics
  const analytics = useMemo(() => {
    if (!finalResults) return null;

    const categories = Array.from(new Set(finalResults.map(p => p.category)));
    const races = getRaceColumns();
    
    const categoryStats = categories.map(category => {
      const categoryParticipants = finalResults.filter(p => p.category === category);
      const avgPoints = categoryParticipants.reduce((sum, p) => sum + getEffectivePoints(p), 0) / categoryParticipants.length;
      const topScorer = categoryParticipants.reduce((top, p) => getEffectivePoints(p) > getEffectivePoints(top) ? p : top, categoryParticipants[0]);
      
      return {
        category,
        participantCount: categoryParticipants.length,
        averagePoints: Math.round(avgPoints * 10) / 10,
        topScorer: topScorer ? { name: topScorer.name, points: getEffectivePoints(topScorer) } : null
      };
    });

    const raceStats = races.map(raceId => {
      const raceParticipants = finalResults.filter(p => p.races[raceId]);
      const totalPoints = raceParticipants.reduce((sum, p) => sum + (p.races[raceId]?.points || 0), 0);
      const avgPoints = totalPoints / raceParticipants.length;
      
      return {
        raceId,
        participantCount: raceParticipants.length,
        totalPointsAwarded: totalPoints,
        averagePoints: Math.round(avgPoints * 10) / 10
      };
    });

    return {
      totalParticipants: finalResults.length,
      totalRaces: races.length,
      categoryStats,
      raceStats,
      topScorer: finalResults.reduce((top, p) => getEffectivePoints(p) > getEffectivePoints(top) ? p : top, finalResults[0])
    };
  }, [finalResults, getRaceColumns, getEffectivePoints]);

  // Enhanced filtering and search logic
  const filteredAndSortedResults = useMemo(() => {
    if (!finalResults) return [];

    let filtered = finalResults.filter(participant => {
      // Search filter
      const matchesSearch = !searchTerm || 
        participant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        participant.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (participant.license && participant.license.toLowerCase().includes(searchTerm.toLowerCase()));

      // Category filter
      const matchesCategory = selectedCategory === 'all' || participant.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });

    // Sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'totalPoints':
          comparison = getEffectivePoints(a) - getEffectivePoints(b);
          break;
        case 'position':
          // Calculate overall position based on total points
          const aPosition = finalResults.findIndex(p => p.id === a.id) + 1;
          const bPosition = finalResults.findIndex(p => p.id === b.id) + 1;
          comparison = aPosition - bPosition;
          break;
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [finalResults, searchTerm, selectedCategory, sortBy, sortOrder, getEffectivePoints]);

  // Toggle race selection for comparison view
  const toggleRaceSelection = (raceId: string) => {
    setSelectedRaces(prev => 
      prev.includes(raceId) 
        ? prev.filter(id => id !== raceId)
        : [...prev, raceId]
    );
  };

  // Handle best-of configuration changes
  const handleBestOfChange = async (newUseBestOf: boolean, newBestOfCount?: number) => {
    setUseBestOf(newUseBestOf);
    setBestOfCount(newBestOfCount);
    
    if (!finalResults) return;
    
    // Recalculate results with new best-of configuration
    const updatedResults = finalResults.map(participant => {
      if (newUseBestOf && newBestOfCount) {
        const raceEntries = Object.entries(participant.races);
        const bestRaces = raceEntries
          .sort(([, a], [, b]) => b.points - a.points)
          .slice(0, newBestOfCount);
        
        const bestOfPoints = bestRaces.reduce((sum, [, race]) => sum + race.points, 0);
        
        return {
          ...participant,
          bestRaces: bestRaces.map(([raceId]) => raceId),
          bestOfPoints
        };
      } else {
        return {
          ...participant,
          bestRaces: Object.keys(participant.races),
          bestOfPoints: participant.totalPoints
        };
      }
    }).sort((a, b) => {
      const aPoints = a.bestOfPoints || a.totalPoints;
      const bPoints = b.bestOfPoints || b.totalPoints;
      return bPoints - aPoints;
    });

    setFinalResults(updatedResults);
    
    // Update app data with new configuration and results
    const config = {
      bestOfCount: newUseBestOf ? newBestOfCount : undefined
    };
    
    const updatedAppData = {
      ...appData,
      finalResults: updatedResults,
      seriesConfiguration: config
    };
    
    setAppData(updatedAppData);
    
    // Only save to series data if we have a complete configuration (both checkbox and count)
    // OR if we're explicitly disabling best-of
    if (appData.currentSeriesMetadata && (!newUseBestOf || (newUseBestOf && newBestOfCount))) {
      try {
        const dataService = (await import('../services/dataService')).default.getInstance();
        const currentSeries = await dataService.loadSeries(appData.currentSeriesMetadata.id);
        
        const updatedSeries = {
          ...currentSeries,
          participants: updatedResults,
          bestOfRaces: newUseBestOf ? newBestOfCount : undefined,
          metadata: {
            ...currentSeries.metadata,
            bestOfRaces: newUseBestOf ? newBestOfCount : undefined,
            lastModified: new Date().toISOString()
          }
        };
        
        await dataService.updateSeries(appData.currentSeriesMetadata.id, updatedSeries);
        
        console.log('Best-of configuration saved to series data:', newUseBestOf ? newBestOfCount : 'disabled');
      } catch (error) {
        console.warn('Failed to save best-of configuration:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="stage-container">
        <div className="loading">
          <div className="spinner"></div>
          <span>Generating final results...</span>
        </div>
      </div>
    );
  }

  // Calculate available races for best-of configuration
  const availableRaceCount = finalResults 
    ? Math.max(...finalResults.map(p => Object.keys(p.races).length))
    : 0;

  return (
    <div className="stage-container">
      <div className="stage-header">
        <h2>📊 Final Results</h2>
        <p>Review the updated series standings and configure series rules.</p>
      </div>

      {finalResults && (
        <>
          {/* Best-of Configuration Section */}
          <div style={{ 
            marginBottom: '2rem', 
            padding: '1.5rem', 
            background: 'rgba(255, 255, 255, 0.05)', 
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
            overflow: 'visible'
          }}>
            <h3>⚙️ Series Rules</h3>
            <p style={{ color: '#718096', marginBottom: '1rem' }}>
              Configure how the championship standings are calculated. Changes will immediately update the results below.
            </p>
            
            <div className="form-group">
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={useBestOf}
                  onChange={(e) => handleBestOfChange(e.target.checked, bestOfCount)}
                  style={{ cursor: 'pointer' }}
                />
                <label style={{ cursor: 'pointer', margin: 0, whiteSpace: 'nowrap' }} onClick={() => handleBestOfChange(!useBestOf, bestOfCount)}>
                  Use "Best of X" scoring
                </label>
              </div>
              
              {useBestOf && (
                <div style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                  <label>Count the best </label>
                  <select
                    value={bestOfCount || ''}
                    onChange={(e) => {
                      const count = e.target.value ? parseInt(e.target.value) : undefined;
                      setBestOfCount(count);
                      handleBestOfChange(true, count);
                    }}
                    className="form-control"
                    style={{ 
                      width: '80px', 
                      display: 'inline-block', 
                      margin: '0 0.5rem',
                      position: 'relative',
                      zIndex: 1000
                    }}
                  >
                    <option value="">Select</option>
                    {Array.from({ length: availableRaceCount }, (_, i) => i + 1).map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                  <label> races out of {availableRaceCount} total</label>
                </div>
              )}
            </div>

            {useBestOf && bestOfCount && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                background: 'rgba(34, 84, 61, 0.1)', 
                border: '1px solid rgba(34, 84, 61, 0.3)', 
                borderRadius: '8px' 
              }}>
                <strong>📊 Best of {bestOfCount}</strong> - Only the top {bestOfCount} race results will count towards the championship.
              </div>
            )}
          </div>

          {/* Results Header */}
          <div style={{ marginBottom: '1rem' }}>
            <h3>🏆 Series Standings by Category</h3>
          </div>

          <div className="file-info">
            <h4>📈 Summary</h4>
            <p>
              Total participants: {finalResults.length}<br />
              Categories: {sortedCategories.length}<br />
              Races in series: {getRaceColumns().length}<br />
              Race columns: {getRaceColumns().join(', ')}<br />
              Latest race: {appData.newRaceData?.raceName}
              {appData.seriesConfiguration?.bestOfCount && (
                <>
                  <br />
                  <strong>Best of {appData.seriesConfiguration.bestOfCount}</strong> scoring is active
                </>
              )}
            </p>
          </div>

          {/* Render different view modes */}
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.05)', 
            padding: '1.5rem', 
            borderRadius: '12px', 
            marginBottom: '2rem',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            {/* View Mode Toggle */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>View Mode</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {(['table', 'comparison', 'analytics'] as const).map(mode => (
                  <button
                    key={mode}
                    className={`btn ${viewMode === mode ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setViewMode(mode)}
                    style={{ padding: '0.5rem 1rem' }}
                  >
                    {mode === 'table' && '📊 Table View'}
                    {mode === 'comparison' && '🔄 Race Comparison'}
                    {mode === 'analytics' && '📈 Analytics'}
                  </button>
                ))}
              </div>
            </div>

            {/* Search and Filters */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              {/* Search */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Search</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, category, or license..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    fontSize: '0.9rem',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Category Filter */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Category</label>
                <select
                  className="form-control"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{ 
                    fontSize: '0.9rem',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="all">All Categories</option>
                  {availableCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Sort By</label>
                <select
                  className="form-control"
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [newSortBy, newSortOrder] = e.target.value.split('-') as [typeof sortBy, typeof sortOrder];
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder);
                  }}
                  style={{ 
                    fontSize: '0.9rem',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="totalPoints-desc">Total Points (High to Low)</option>
                  <option value="totalPoints-asc">Total Points (Low to High)</option>
                  <option value="name-asc">Name (A to Z)</option>
                  <option value="name-desc">Name (Z to A)</option>
                  <option value="position-asc">Position (Best to Worst)</option>
                </select>
              </div>

              {/* Results Count */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Results</label>
                <div style={{ 
                  padding: '0.75rem', 
                  background: 'rgba(255, 255, 255, 0.1)', 
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  {filteredAndSortedResults.length} of {finalResults?.length || 0}
                </div>
              </div>
            </div>
          </div>

          {/* Analytics View */}
          {viewMode === 'analytics' && analytics && (
            <div style={{ marginBottom: '2rem' }}>
              <h3>📈 Series Analytics</h3>
              
              {/* Overall Stats */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '1rem', 
                marginBottom: '2rem' 
              }}>
                <div className="analytics-card">
                  <h4>🏆 Top Performer</h4>
                  <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                    {analytics.topScorer.name}
                  </div>
                  <div style={{ color: '#718096' }}>
                    {getEffectivePoints(analytics.topScorer)} points total
                  </div>
                </div>
                
                <div className="analytics-card">
                  <h4>👥 Participation</h4>
                  <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                    {analytics.totalParticipants} participants
                  </div>
                  <div style={{ color: '#718096' }}>
                    Across {analytics.categoryStats.length} categories
                  </div>
                </div>
                
                <div className="analytics-card">
                  <h4>🏁 Races</h4>
                  <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                    {analytics.totalRaces} races completed
                  </div>
                  <div style={{ color: '#718096' }}>
                    {analytics.raceStats.reduce((sum, r) => sum + r.totalPointsAwarded, 0)} points awarded
                  </div>
                </div>
              </div>

              {/* Category Statistics */}
              <div style={{ marginBottom: '2rem' }}>
                <h4>📊 Category Statistics</h4>
                <div className="category-table">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Participants</th>
                        <th>Average Points</th>
                        <th>Top Performer</th>
                        <th>Top Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.categoryStats.map(stat => (
                        <tr key={stat.category}>
                          <td><strong>{stat.category}</strong></td>
                          <td>{stat.participantCount}</td>
                          <td>{stat.averagePoints} pts</td>
                          <td>{stat.topScorer?.name || 'N/A'}</td>
                          <td><span className="points-value">{stat.topScorer?.points || 0} pts</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Race Statistics */}
              <div>
                <h4>🏁 Race Statistics</h4>
                <div className="category-table">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Race</th>
                        <th>Participants</th>
                        <th>Total Points Awarded</th>
                        <th>Average Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.raceStats.map(stat => (
                        <tr key={stat.raceId}>
                          <td><strong>{stat.raceId.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}</strong></td>
                          <td>{stat.participantCount}</td>
                          <td><span className="points-value">{stat.totalPointsAwarded} pts</span></td>
                          <td>{stat.averagePoints} pts</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Race Comparison View */}
          {viewMode === 'comparison' && (
            <div style={{ marginBottom: '2rem' }}>
              <h3>🔄 Race Comparison</h3>
              
              {/* Race Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4>Select Races to Compare</h4>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {getRaceColumns().map(raceId => (
                    <button
                      key={raceId}
                      className={`btn ${selectedRaces.includes(raceId) ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => toggleRaceSelection(raceId)}
                      style={{ padding: '0.5rem 1rem' }}
                    >
                      {raceId.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                    </button>
                  ))}
                </div>
                {selectedRaces.length === 0 && (
                  <p style={{ color: '#718096', marginTop: '0.5rem' }}>
                    Select at least one race to view comparison data
                  </p>
                )}
              </div>

              {/* Comparison Table */}
              {selectedRaces.length > 0 && (
                <div className="category-table">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Participant</th>
                        <th>Category</th>
                        {selectedRaces.map(raceId => (
                          <th key={raceId} style={{ minWidth: '120px' }}>
                            {raceId.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                          </th>
                        ))}
                        <th>Selected Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedResults
                        .filter(participant => selectedRaces.some(raceId => participant.races[raceId]))
                        .map(participant => {
                          const selectedTotal = selectedRaces.reduce((sum, raceId) => 
                            sum + (participant.races[raceId]?.points || 0), 0
                          );
                          
                          return (
                            <tr key={participant.id}>
                              <td><strong>{participant.name}</strong></td>
                              <td>{participant.category}</td>
                              {selectedRaces.map(raceId => {
                                const raceData = participant.races[raceId];
                                return (
                                  <td key={raceId}>
                                    {raceData ? (
                                      <div>
                                        <div><strong>{raceData.points}pts</strong></div>
                                        <small style={{ color: '#718096' }}>P{raceData.position}</small>
                                      </div>
                                    ) : (
                                      <span style={{ color: '#a0aec0' }}>-</span>
                                    )}
                                  </td>
                                );
                              })}
                              <td><span className="points-value">{selectedTotal} pts</span></td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Enhanced Table View */}
          {viewMode === 'table' && (
            <div style={{ marginBottom: '2rem' }}>
              {/* Group filtered results by category */}
              {availableCategories
                .filter(category => selectedCategory === 'all' || selectedCategory === category)
                .map(category => {
                  const categoryParticipants = filteredAndSortedResults
                    .filter(p => p.category === category)
                    .map((participant, index) => ({ ...participant, categoryPosition: index + 1 }));
                  
                  if (categoryParticipants.length === 0) return null;

                  return (
                    <div key={category} className="category-section">
                      <div className="category-header">
                        <h4>🏷️ {category}</h4>
                        <span className="category-count">{categoryParticipants.length} participants</span>
                      </div>
                      
                      <div className="category-table">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Position</th>
                              <th>Name</th>
                              <th>License</th>
                              <th>
                                {getCurrentBestOfCount()
                                  ? `Best ${getCurrentBestOfCount()} Points`
                                  : 'Total Points'
                                }
                              </th>
                              {getCurrentBestOfCount() && (
                                <th>Total Points</th>
                              )}
                              {getRaceColumns().map(raceId => (
                                <th key={raceId} style={{ minWidth: '100px' }}>
                                  {raceId.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {categoryParticipants.map((participant) => {
                              const originalPosition = finalResults
                                .filter(p => p.category === category)
                                .sort((a, b) => getEffectivePoints(b) - getEffectivePoints(a))
                                .findIndex(p => p.id === participant.id) + 1;
                              
                              return (
                                <tr key={participant.id}>
                                  <td>
                                    <div className="position-cell">
                                      <strong>{originalPosition}</strong>
                                      {originalPosition === 1 && <span className="medal-icon">🥇</span>}
                                      {originalPosition === 2 && <span className="medal-icon">🥈</span>}
                                      {originalPosition === 3 && <span className="medal-icon">🥉</span>}
                                    </div>
                                  </td>
                                  <td><strong>{participant.name}</strong></td>
                                  <td style={{ color: '#718096' }}>{participant.license || '-'}</td>
                                  <td>
                                    <span className="points-value">
                                      {getCurrentBestOfCount()
                                        ? (participant.bestOfPoints || participant.totalPoints)
                                        : participant.totalPoints} pts
                                    </span>
                                  </td>
                                  {getCurrentBestOfCount() && (
                                    <td><span className="points-value">{participant.totalPoints} pts</span></td>
                                  )}
                                  {getRaceColumns().map(raceId => {
                                    const raceData = participant.races[raceId];
                                    return (
                                      <td key={raceId}>
                                        {raceData ? (
                                          <div>
                                            <div><strong>{raceData.points}pts</strong></div>
                                            <small style={{ color: '#718096' }}>P{raceData.position}</small>
                                          </div>
                                        ) : (
                                          <span style={{ color: '#a0aec0' }}>-</span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          <div className="success" style={{ marginTop: '2rem' }}>
            <strong>🎉 Processing Complete!</strong><br />
            Your race series points have been successfully calculated and updated. 
            The exported CSV files use a standardized format that can be easily re-imported 
            for future race processing.
          </div>
        </>
      )}

      <div className="stage-actions">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <button 
            className="btn btn-secondary" 
            onClick={exportToCSV}
            style={{ fontSize: '0.9rem' }}
          >
            📄 Export Series Standings
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={onRestart}
          >
            🔄 Process Another Race
          </button>
          
          <button 
            className="btn btn-primary" 
            onClick={onNext}
          >
            Next: Publish →
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsPreview; 